import { instructionStrings } from '@data/instruction';
import { companyItem } from '@models/Company';
import {
  areContactsEqual,
  contactForm,
  contactFormLoose,
  contactFormInitialValues,
  patchContact,
} from '@models/Contact';
import { instructionStatusName, instructionStatus } from '@models/Instruction';
import { adminUser } from '@models/User';
import { createValidate, mergeErrors, requiredStringField, stringField } from '@utils/forms';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export const deliveryTruckName = t.union([
  t.literal(''),
  t.literal('その他'),
  t.literal('mix'),
  t.literal('4T'),
  t.literal('4Tx2'),
  t.literal('10T'),
]);

export type DeliveryTruckName = t.TypeOf<typeof deliveryTruckName>;

export const deliveryTruck = t.type({
  name: deliveryTruckName,
  delivery_fee: t.number, // non_target_tax_price + target_tax_price
  delivery_date: h.omittable(t.string), // In YYYY-MM-DD
  delivery_time: h.omittable(t.string), // Example: 配達当日朝9:00, any string
  consignee_price: t.number,
  shipper_price: t.number,
});

export type DeliveryTruck = t.TypeOf<typeof deliveryTruck>;

export const deliveryInstructionBase = t.type({
  is_receiver_checked: t.boolean,
  truck: deliveryTruckName,
  preferred_delivery_date: h.omittable(t.string),
  preferred_delivery_time: t.string,
  has_special_note: t.boolean,
  special_note: t.string,
});

export const deliveryDateOptions = t.partial({
  '': t.array(t.string),
  その他: t.array(t.string),
  mix: t.array(t.string),
  '4T': t.array(t.string),
  '4Tx2': t.array(t.string),
  '10T': t.array(t.string),
});

export const deliveryInstruction = t.type({
  ...deliveryInstructionBase.props,
  cutoff_date: t.string,
  status: t.array(instructionStatus),
  receiver: t.union([contactFormLoose, t.null]),
  old_receiver: contactFormLoose,
  truck_options: t.array(deliveryTruck),
  delivery_date_options: deliveryDateOptions,
  created_at: t.string,
  updated_at: t.string,
  writer: h.omittable(adminUser),
  writer_company: h.omittable(companyItem),
  writer_id: h.omittable(t.number),
  simulator_error: h.omittable(t.string),
  cargo_changed: h.omittable(t.boolean),
  delivery_requests: t.array(
    t.type({
      id: t.number,
    }),
  ),
});

export type DeliveryInstruction = t.TypeOf<typeof deliveryInstruction>;

export const deliveryInstructionListItem = t.type({
  d2d_no: t.string,
  cutoff_date: t.string,
  status: t.array(instructionStatus),
  eta: h.omittable(t.string),
  etd: h.omittable(t.string),
  created_at: t.string,
  updated_at: t.string,
});

export type DeliveryInstructionListItem = t.TypeOf<typeof deliveryInstructionListItem>;

export const deliveryInstructionList = t.type({
  results: h.array(deliveryInstructionListItem),
  count: t.number,
});

export const deliveryInstructionReceiverForm = h.taggedUnion([
  {
    tag: t.literal('RECEIVER_CONFIRMED'),
  },
  {
    tag: t.literal('RECEIVER_CHANGE'),
    receiver: contactForm,
  },
]);

export type DeliveryInstructionReceiverForm = t.TypeOf<typeof deliveryInstructionReceiverForm>;

export const deliveryInstructionReceiverFormLoose = h.taggedUnion([
  {
    tag: t.literal('RECEIVER_CONFIRMED'),
  },
  {
    tag: t.literal('RECEIVER_CHANGE'),
    receiver: contactFormLoose,
  },
]);

export type DeliveryInstructionReceiverFormLoose = t.TypeOf<
  typeof deliveryInstructionReceiverFormLoose
>;

export const deliveryInstructionForm = t.type({
  receiver: deliveryInstructionReceiverForm.withLabel(instructionStrings.receiver),
  truck: requiredStringField(instructionStrings.truckChoice),
  preferred_delivery_date: stringField(instructionStrings.preferredDeliveryDate),
  preferred_delivery_time: requiredStringField(instructionStrings.deliveryTime),
  has_special_note: h.boolean().withLabel(instructionStrings.specialNote),
  special_note: stringField(instructionStrings.specialNoteDetails),
});

export type DeliveryInstructionForm = t.TypeOf<typeof deliveryInstructionForm>;

export const deliveryInstructionFormLoose = t.type({
  receiver: deliveryInstructionReceiverFormLoose,
  truck: stringField(instructionStrings.truckChoice),
  preferred_delivery_date: stringField(instructionStrings.preferredDeliveryDate),
  preferred_delivery_time: stringField(instructionStrings.deliveryTime),
});

export type DeliveryInstructionFormLoose = t.TypeOf<typeof deliveryInstructionFormLoose>;

export const patchDeliveryInstruction = t.partial({
  ...deliveryInstructionBase.props,
  receiver: h.omittable(patchContact),
  status_name: instructionStatusName,
});

export type PatchDeliveryInstruction = t.TypeOf<typeof patchDeliveryInstruction>;

export const adminPatchDeliveryInstruction = t.partial({
  ...deliveryInstructionBase.props,
  receiver: h.omittable(patchContact),
  message: t.string,
  status_name: instructionStatusName,
});

export type AdminPatchDeliveryInstruction = t.TypeOf<typeof adminPatchDeliveryInstruction>;

export const validateDeliveryInstructionForm = (values: DeliveryInstructionForm) => {
  const e = createValidate(deliveryInstructionForm)(values);

  if (e.receiver && values.receiver.tag === 'RECEIVER_CHANGE') {
    e.receiver.tag = instructionStrings.needValidReceiver;
  }

  return mergeErrors(e, validateDeliveryInstructionSpecialNote(values));
};

export const validateDeliveryInstructionSpecialNote = (
  _values: Pick<DeliveryInstructionForm, 'has_special_note' | 'special_note'>,
) => {
  // if (values.has_special_note && values.special_note.trim() === '') {
  //   return {
  //     special_note: formStrings.notEmpty(instructionStrings.specialNoteDetails),
  //   };
  // }

  return {};
};

export const validateDeliveryInstructionFormLoose = (values: DeliveryInstructionForm) => {
  const e = createValidate(deliveryInstructionFormLoose)(values);

  return e;
};

export const mapDeliveryInstructionToDeliveryInstructionForm = (
  d: DeliveryInstruction,
): DeliveryInstructionForm => {
  return {
    receiver: d.is_receiver_checked
      ? {
          tag: 'RECEIVER_CONFIRMED',
        }
      : {
          tag: 'RECEIVER_CHANGE',
          receiver:
            d.receiver == null || areContactsEqual(d.receiver, d.old_receiver)
              ? contactFormInitialValues
              : d.receiver,
        },
    truck: d.truck,
    preferred_delivery_time: d.preferred_delivery_time,
    preferred_delivery_date: d.preferred_delivery_date ?? '',
    has_special_note: d.has_special_note,
    special_note: d.special_note,
  };
};

export const mapDeliveryInstructionFormToPatchDeliveryInstruction = (
  d: DeliveryInstructionFormLoose,
): PatchDeliveryInstruction => {
  return {
    ...d,
    truck: d.truck as DeliveryTruckName,
    preferred_delivery_date: d.preferred_delivery_date || null,
    is_receiver_checked: d.receiver.tag === 'RECEIVER_CONFIRMED',
    receiver:
      d.receiver.tag === 'RECEIVER_CHANGE'
        ? areContactsEqual(d.receiver.receiver, contactFormInitialValues)
          ? null // This means the receiver form is untouched or cancelled (in case of which all fields are initial)
          : d.receiver.receiver
        : null,
  };
};

export const getDeliveryErrorLabels = (errors: any) =>
  [
    'receiver' in errors && deliveryInstructionForm.props.receiver.label,
    'truck' in errors && deliveryInstructionForm.props.truck.label,
    'preferred_delivery_date' in errors &&
      deliveryInstructionForm.props.preferred_delivery_date.label,
    'preferred_delivery_time' in errors &&
      deliveryInstructionForm.props.preferred_delivery_time.label,
    'special_note' in errors && deliveryInstructionForm.props.special_note.label,
    'has_special_note' in errors && deliveryInstructionForm.props.has_special_note.label,
    'insurance' in errors && 'company' in errors.insurance && instructionStrings.insuranceCompany,
    'insurance' in errors &&
      'policy_no' in errors.insurance &&
      instructionStrings.insurancePolicyNo,
    'insurance' in errors &&
      ('start' in errors.insurance || 'end' in errors.insurance) &&
      instructionStrings.insurancePeriod,
  ].filter((s): s is string => Boolean(s));

export const deliveryInstructionListQueryParams = t.partial({
  page: h.number().cast(),
  d2d_no: t.string,
  status: t.array(t.string),
});

export type DeliveryInstructionListQueryParams = t.TypeOf<
  typeof deliveryInstructionListQueryParams
>;

export const deliveryInstructionListSearchType = t.partial({
  ...deliveryInstructionListQueryParams.props,
  status: t.string,
});

export const deliveryInstructionFormInitialValues: DeliveryInstructionForm = {
  receiver: { tag: 'RECEIVER_CONFIRMED' },
  truck: '',
  preferred_delivery_date: '',
  preferred_delivery_time: '',
  has_special_note: false,
  special_note: '',
};
